import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';

import { HTTPError } from 'src/api/createApiClient.types';
import { cloudConnectSubscriptionApi } from 'src/api/endpoints';

import type {
  CheckoutConfirmation,
  CheckoutPromotionPreviewRequest,
  CheckoutSessionRequest,
  CheckoutSessionStatusResponse,
  PriceParams,
  Prices,
} from '../endpoints/cloudConnect.types';
import { useCountry } from './country';
import { queryKeys } from './queryKeys';

// Data layer for the checkout V2 address, payment, checkout-return, and
// confirmation steps.

const CHECKOUT_CONFIRMATION_POLLING_INTERVAL = 1000;
const CHECKOUT_CONFIRMATION_MAX_POLLING_ATTEMPTS = 30;

const CHECKOUT_SESSION_STATUS_POLLING_INTERVAL = 1000;
const CHECKOUT_SESSION_STATUS_MAX_POLLING_ATTEMPTS = 15;

// useCheckoutSession creates the underlying Stripe Checkout Session using the
// cloud connect API
export const useCheckoutSession = () => {
  return useMutation({
    mutationFn: (params: CheckoutSessionRequest) =>
      cloudConnectSubscriptionApi.createCheckoutSession(params),
  });
};

export const useCheckoutPromotionPreview = () =>
  useMutation({
    mutationFn: (params: CheckoutPromotionPreviewRequest) =>
      cloudConnectSubscriptionApi.previewCheckoutPromotion(params),
  });

const createCheckoutSessionStatusOptions = (params: {
  sessionId: string;
  country: string;
}) =>
  queryOptions({
    queryKey: queryKeys.checkoutV2.sessionStatus(params),
    queryFn: (): Promise<CheckoutSessionStatusResponse> =>
      cloudConnectSubscriptionApi.getCheckoutSession(params),
  });

// useCheckoutSessionStatus looks up the outcome of a checkout session after
// a redirect-based payment (e.g. PayPal) sends the customer back to us.
export const useCheckoutSessionStatus = (params: {
  sessionId?: string;
  country?: string;
}) =>
  useQuery({
    ...createCheckoutSessionStatusOptions({
      sessionId: params.sessionId ?? '',
      country: params.country ?? '',
    }),
    enabled: !!params.sessionId && !!params.country,
    // The webhook that flips the session to `complete` can lag by a few
    // seconds after the customer returns from a redirect payment. Poll while
    // the session is still `open` so we don't misclassify a successful
    // payment as a failure.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const attempts = query.state.dataUpdateCount;
      if (
        status === 'open' &&
        attempts < CHECKOUT_SESSION_STATUS_MAX_POLLING_ATTEMPTS
      ) {
        return CHECKOUT_SESSION_STATUS_POLLING_INTERVAL;
      }
      return false;
    },
  });

const createCheckoutConfirmationOptions = () =>
  queryOptions({
    queryKey: queryKeys.checkoutV2.confirmation,
    queryFn: (): Promise<CheckoutConfirmation> =>
      cloudConnectSubscriptionApi.getCheckoutConfirmation(),
  });

// useCheckoutConfirmationV2 retrieves the subscription created by checkout.
// The confirmation record may lag behind the Stripe redirect while webhooks
// finish processing, so retry 404s briefly before showing an error.
export const useCheckoutConfirmationV2 = () =>
  useQuery({
    ...createCheckoutConfirmationOptions(),
    retry(failureCount, error) {
      if (!(error instanceof HTTPError)) {
        return false;
      }

      if (error.response.status === 404) {
        return failureCount < CHECKOUT_CONFIRMATION_MAX_POLLING_ATTEMPTS;
      }

      return error.response.status >= 500 && failureCount < 3;
    },
    retryDelay: CHECKOUT_CONFIRMATION_POLLING_INTERVAL,
  });

const createPriceOptions = (params: PriceParams) =>
  queryOptions({
    queryKey: queryKeys.checkoutV2.price(params),
    queryFn: (): Promise<Prices | null> =>
      cloudConnectSubscriptionApi.getPrice(params),
  });

// usePrice retrieves the price based on the country
export const usePrice = () => {
  const country = useCountry();
  const countryValue = country.data?.value;

  return useQuery({
    ...createPriceOptions({
      country: countryValue ?? '',
      productID: 'connect',
    }),
    enabled: country.isSuccess && !country.isFetching && !!countryValue,
  });
};

const createTrialOfferOptions = () =>
  queryOptions({
    queryKey: queryKeys.checkoutV2.trialOffer,
    queryFn: () => cloudConnectSubscriptionApi.getTrialOffer(),
  });

// useTrialOffer retrieves a trial offer from cloud-connect if one exists.
export const useTrialOffer = () => {
  return useQuery(createTrialOfferOptions());
};
